import { useState, useRef, useEffect, useCallback } from "react";
import { unstable_batchedUpdates } from "react-dom";
import axios from "../axios";
import * as url from "../utils/url";
import {
  TRANSLATE_ERROR_GENERIC,
  TRANSLATE_MODE_ENGLISH,
  TRANSLATE_MODE_ORIGINAL,
  TRANSLATE_OUTCOME_CANCELLED,
  TRANSLATE_OUTCOME_DONE,
  TRANSLATE_OUTCOME_ERROR,
  TRANSLATE_OUTCOME_REVERT,
  TRANSLATE_REQUEST_TIMEOUT_MS,
  TRANSLATE_STATUS_DONE,
  TRANSLATE_STATUS_ERROR,
  TRANSLATE_STATUS_IDLE,
  createRunState,
  mergeTranslations,
  pickTranslatedText,
  readVisibleTranslations,
  runTranslatePass,
  signatureOf,
} from "../utils/translateResponsesCore";

const EMPTY_PROGRESS = {
  checked: 0,
  translated: 0,
  failed: 0,
  pending: 0,
  notice: null,
};

const unwrapData = (res) => (res && res.data ? res.data.data : null);

const toIdList = (resultIds) =>
  (Array.isArray(resultIds) ? resultIds : [])
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map((id) => String(id));

// Shared By User "Translate" state (Messages + Survey/KC). Nothing is fetched on mount: English
// starts a pass, and rows that appear later only trigger a read, never new AI work.
const useTranslatedResponses = ({ triviaId, resultIds }) => {
  const [mode, setModeState] = useState(TRANSLATE_MODE_ORIGINAL);
  const [status, setStatus] = useState(TRANSLATE_STATUS_IDLE);
  const [progress, setProgress] = useState(EMPTY_PROGRESS);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);

  const mapRef = useRef({});
  const noticeRef = useRef(null);
  const runRef = useRef(0);
  const mountedRef = useRef(true);
  const timersRef = useRef([]);
  const runStateRef = useRef(createRunState());
  const triviaIdRef = useRef(triviaId);
  const modeRef = useRef(TRANSLATE_MODE_ORIGINAL);
  const statusRef = useRef(TRANSLATE_STATUS_IDLE);
  const visibleIdsRef = useRef([]);

  visibleIdsRef.current = toIdList(resultIds);
  const visibleSignature = signatureOf(visibleIdsRef.current);

  const clearTimers = useCallback(() => {
    const pending = timersRef.current;
    timersRef.current = [];
    pending.forEach((entry) => {
      clearTimeout(entry.id);
      entry.resolve();
    });
  }, []);

  // Bumping the token orphans any in-flight run; cleared timers resolve early.
  const stopRun = useCallback(() => {
    runRef.current += 1;
    clearTimers();
  }, [clearTimers]);

  const wait = useCallback((ms) => {
    return new Promise((resolve) => {
      const entry = { id: null, resolve: resolve };
      entry.id = setTimeout(() => {
        timersRef.current = timersRef.current.filter((item) => item !== entry);
        resolve();
      }, ms);
      timersRef.current.push(entry);
    });
  }, []);

  const setStatusBoth = useCallback((next) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const setModeBoth = useCallback((next) => {
    modeRef.current = next;
    setModeState(next);
  }, []);

  const applyTranslations = useCallback((translations) => {
    if (mergeTranslations(mapRef.current, translations) > 0) {
      setVersion((v) => v + 1);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopRun();
    };
  }, [stopRun]);

  // A different Trivvy starts clean: no run, no map, back to Original.
  useEffect(() => {
    if (triviaIdRef.current === triviaId) {
      return;
    }
    triviaIdRef.current = triviaId;
    stopRun();
    mapRef.current = {};
    noticeRef.current = null;
    runStateRef.current = createRunState();
    setModeBoth(TRANSLATE_MODE_ORIGINAL);
    setStatusBoth(TRANSLATE_STATUS_IDLE);
    setProgress(EMPTY_PROGRESS);
    setError(null);
    setVersion((v) => v + 1);
  }, [triviaId, stopRun, setModeBoth, setStatusBoth]);

  const readTranslations = useCallback(
    (ids) =>
      axios
        .get(url.getResponseTranslations(triviaId), {
          params: { resultIds: ids.join(",") },
          timeout: TRANSLATE_REQUEST_TIMEOUT_MS,
        })
        .then(unwrapData),
    [triviaId]
  );

  // Read-only refresh of the rows on screen (no AI work); returns a canceller.
  const refreshVisible = useCallback(() => {
    let cancelled = false;
    const isCancelled = () =>
      cancelled || !mountedRef.current || modeRef.current !== TRANSLATE_MODE_ENGLISH;
    readVisibleTranslations(readTranslations, visibleIdsRef.current, isCancelled, applyTranslations).catch(
      () => {
        // Keep showing whatever is on screen; the original text is never at risk.
      }
    );
    return () => {
      cancelled = true;
    };
  }, [readTranslations, applyTranslations]);

  const startRun = useCallback(
    (resume) => {
      stopRun();
      const runId = runRef.current;
      const isCancelled = () => runId !== runRef.current || !mountedRef.current;
      if (!resume) {
        runStateRef.current = createRunState();
      }
      const runState = runStateRef.current;

      setModeBoth(TRANSLATE_MODE_ENGLISH);
      setError(null);
      if (!resume) {
        // A new pass starts from originals: English from a past pass is shown only once re-read.
        mapRef.current = {};
        setVersion((v) => v + 1);
        noticeRef.current = null;
        setProgress(EMPTY_PROGRESS);
      }

      // Async callbacks run outside React events: batch each update into one render.
      const onUpdate = (patch) => {
        if (isCancelled()) {
          return;
        }
        unstable_batchedUpdates(() => {
          if (patch.translations) {
            applyTranslations(patch.translations);
          }
          if (patch.status) {
            setStatusBoth(patch.status);
          }
          const hasNotice = Object.prototype.hasOwnProperty.call(patch, "notice");
          if (hasNotice) {
            noticeRef.current = patch.notice;
          }
          if (hasNotice || patch.progress) {
            setProgress((prev) =>
              Object.assign({}, prev, patch.progress || {}, { notice: noticeRef.current })
            );
          }
        });
      };

      const deps = {
        process: (cursor) =>
          axios
            .post(
              url.processResponseTranslations(triviaId),
              cursor ? { cursor: cursor } : {},
              { timeout: TRANSLATE_REQUEST_TIMEOUT_MS }
            )
            .then(unwrapData),
        read: readTranslations,
        getVisibleIds: () => visibleIdsRef.current,
        now: Date.now,
        wait: wait,
        isCancelled: isCancelled,
        onUpdate: onUpdate,
      };

      const finish = (result) => {
        if (isCancelled() || result.outcome === TRANSLATE_OUTCOME_CANCELLED) {
          return;
        }
        unstable_batchedUpdates(() => {
          noticeRef.current = null;
          setProgress((prev) => Object.assign({}, prev, { notice: null }));
          if (result.outcome === TRANSLATE_OUTCOME_DONE) {
            setStatusBoth(TRANSLATE_STATUS_DONE);
            // Rows that appeared while the final read was in flight still need one read.
            if (signatureOf(visibleIdsRef.current) !== runState.lastReadSignature) {
              refreshVisible();
            }
            return;
          }
          setStatusBoth(TRANSLATE_STATUS_ERROR);
          setError({ kind: result.kind || TRANSLATE_ERROR_GENERIC });
          if (result.outcome === TRANSLATE_OUTCOME_REVERT) {
            setModeBoth(TRANSLATE_MODE_ORIGINAL);
          }
        });
      };

      runTranslatePass(deps, runState).then(finish, () => {
        finish({ outcome: TRANSLATE_OUTCOME_ERROR, kind: TRANSLATE_ERROR_GENERIC });
      });
    },
    [triviaId, stopRun, wait, readTranslations, applyTranslations, refreshVisible, setModeBoth, setStatusBoth]
  );

  // Rows that appear after a pass (filters, a new page) only need a read, never AI work.
  useEffect(() => {
    if (
      modeRef.current !== TRANSLATE_MODE_ENGLISH ||
      statusRef.current !== TRANSLATE_STATUS_DONE ||
      visibleIdsRef.current.length === 0
    ) {
      return undefined;
    }
    return refreshVisible();
  }, [visibleSignature, refreshVisible]);

  const setMode = useCallback(
    (next) => {
      if (next === TRANSLATE_MODE_ENGLISH) {
        startRun(false);
        return;
      }
      stopRun();
      noticeRef.current = null;
      setModeBoth(TRANSLATE_MODE_ORIGINAL);
      setStatusBoth(TRANSLATE_STATUS_IDLE);
      setError(null);
      setProgress(EMPTY_PROGRESS);
    },
    [startRun, stopRun, setModeBoth, setStatusBoth]
  );

  const retry = useCallback(() => {
    startRun(true);
  }, [startRun]);

  // Original, or a failed run, shows the source text; `version` keys the map.
  const active = mode === TRANSLATE_MODE_ENGLISH && status !== TRANSLATE_STATUS_ERROR;
  const getText = useCallback(
    (resultId, questionId, original) => {
      if (!active) {
        return original;
      }
      return pickTranslatedText(mapRef.current, resultId, questionId, original);
    },
    [active, version]
  );

  return {
    mode: mode,
    setMode: setMode,
    status: status,
    progress: progress,
    error: error,
    getText: getText,
    retry: retry,
  };
};

export default useTranslatedResponses;
